import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, LogOut, Star, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const STATUS_BADGE = {
  "Pendente": "border-[#D2992266] bg-[#D299221a] text-[#D29922]",
  "Aprovada": "border-[#3FB95066] bg-[#3FB9501a] text-[#3FB950]",
  "Rejeitada": "border-[#F8514966] bg-[#F851491a] text-[#F85149]",
};

const Stars = ({ value }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={13} className={i <= value ? "fill-gold text-gold" : "text-mist/40"} />
    ))}
  </span>
);

const btnOutline =
  "rounded-full border border-white/15 px-4 py-2 font-mono text-[11px] tracking-widest text-sand/70 transition-colors";

export default function AdminReviews() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [names, setNames] = useState({});
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .get("/reviews/all")
      .then((r) => {
        setReviews(r.data);
        const n = {};
        r.data.forEach((rev) => (n[rev.id] = rev.display_name || ""));
        setNames(n);
      })
      .catch((err) => {
        if (err.response?.status === 401) navigate("/admin");
      });
  }, [navigate]);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        load();
      })
      .catch(() => navigate("/admin"));
  }, [navigate, load]);

  const patch = async (id, body) => {
    setError("");
    try {
      await api.patch(`/reviews/${id}`, body);
      load();
    } catch (err) {
      if (err.response?.status === 401) return navigate("/admin");
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminar a avaliação de "${r.name}"?`)) return;
    try {
      await api.delete(`/reviews/${r.id}`);
      load();
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin");
    }
  };

  const handleLogout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin");
  };

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-basalt" data-testid="admin-reviews-loading">
        <p className="font-mono text-xs tracking-widest text-mist">A verificar sessão…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-basalt font-sans text-sand" data-testid="admin-reviews-page">
      <header className="border-b border-white/10 bg-surface/60 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div>
            <p className="flex items-center">
              <img src="/logo.svg" alt="AZORUniqueHome" className="h-6 w-auto" />
              <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.3em] text-mist">Admin</span>
            </p>
            <p className="mt-0.5 font-mono text-[11px] text-mist">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              data-testid="admin-back-projects"
              className={`${btnOutline} flex items-center gap-2 hover:border-terracotta hover:text-terracotta`}
            >
              <ArrowLeft size={13} /> Projetos
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-reviews-logout"
              className={`${btnOutline} flex items-center gap-2 hover:border-terracotta hover:text-terracotta`}
            >
              <LogOut size={13} /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="font-display text-2xl sm:text-3xl" data-testid="admin-reviews-title">
          Avaliações
          <span className="ml-3 align-middle font-mono text-sm tracking-widest text-mist">
            ({reviews.length})
          </span>
        </h1>

        {error && (
          <p className="mt-4 rounded-md border border-terracotta/40 bg-terracotta/10 px-3 py-2 text-xs text-terracotta" data-testid="admin-reviews-error">
            {error}
          </p>
        )}

        <div className="mt-8 space-y-4" data-testid="admin-reviews-list">
          {reviews.length === 0 && (
            <p className="rounded-lg border border-dashed border-white/10 p-10 text-center font-mono text-[11px] uppercase tracking-widest text-mist" data-testid="admin-reviews-empty">
              Ainda não existem avaliações submetidas.
            </p>
          )}
          {reviews.map((r) => (
            <div key={r.id} className="rounded-lg border border-white/10 bg-surface p-5" data-testid={`admin-review-${r.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-display text-lg text-sand">{r.name}</p>
                <Stars value={r.rating} />
                <span className={`rounded-full border px-3 py-1 font-mono text-[10px] uppercase tracking-widest ${STATUS_BADGE[r.status] || ""}`} data-testid={`admin-review-status-${r.id}`}>
                  {r.status}
                </span>
              </div>
              {r.project && (
                <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-mist">{r.project}</p>
              )}
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-mist">{r.comment}</p>
              <p className="mt-2 font-mono text-[10px] tracking-widest text-sand/30">
                {new Date(r.created_at).toLocaleDateString("pt-PT")} · Autorização de publicação: {r.consent ? "sim" : "não"}
              </p>

              <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
                <input
                  value={names[r.id] ?? ""}
                  onChange={(e) => setNames({ ...names, [r.id]: e.target.value })}
                  placeholder="Nome a exibir (opcional)"
                  className="w-56 rounded-md border border-white/10 bg-basalt px-3 py-2 text-sm text-sand outline-none transition-colors focus:border-terracotta"
                  data-testid={`admin-review-name-${r.id}`}
                />
                <button
                  onClick={() => patch(r.id, { display_name: names[r.id] ?? "" })}
                  className={`${btnOutline} hover:border-terracotta hover:text-terracotta`}
                  data-testid={`admin-review-savename-${r.id}`}
                >
                  Guardar nome
                </button>
                {r.status !== "Aprovada" && (
                  <button
                    onClick={() => patch(r.id, { status: "Aprovada", display_name: names[r.id] ?? "" })}
                    className="rounded-full border border-[#3FB95066] px-4 py-2 font-mono text-[11px] tracking-widest text-[#3FB950] transition-colors hover:bg-[#3FB9501a]"
                    data-testid={`admin-review-approve-${r.id}`}
                  >
                    Aprovar
                  </button>
                )}
                {r.status !== "Rejeitada" && (
                  <button
                    onClick={() => patch(r.id, { status: "Rejeitada", display_name: names[r.id] ?? "" })}
                    className="rounded-full border border-[#F8514966] px-4 py-2 font-mono text-[11px] tracking-widest text-[#F85149] transition-colors hover:bg-[#F851491a]"
                    data-testid={`admin-review-reject-${r.id}`}
                  >
                    Rejeitar
                  </button>
                )}
                <button
                  onClick={() => remove(r)}
                  className="ml-auto rounded-md border border-white/15 p-2 text-sand/60 transition-colors hover:border-red-500 hover:text-red-400"
                  aria-label={`Eliminar avaliação de ${r.name}`}
                  data-testid={`admin-review-delete-${r.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
